import React, { useState } from 'react';
import PropTypes from 'prop-types';

// Constants
import { T, FONT_MONO } from '../constants/tokens';
import { formatWhen, heartbeatOf } from '../lib/badgeTimes';
import DefaultPortrait from '../images/DefaultPortrait.png';

const heading = {
  fontFamily: FONT_MONO,
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  textTransform: 'uppercase',
  color: T.body
};

/**
 * Who is in or near a room, from the badges last located there.
 *
 * Two groups, because they mean different things in an emergency. A badge
 * that is still being heard from puts its holder here now. One that has gone
 * quiet only says where its holder was when it was last located, so those are
 * listed apart and muted rather than counted as present.
 *
 * @param {{ badges: import('../lib').Badge[] }} props
 */
export default function StaffWidget({ badges, style = {} }) {
  const held = (badges ?? []).filter((badge) => badge.getBelongsTo() != null);
  if (held.length === 0) return null;

  // Most recently located first.
  const byRecent = (a, b) => (b.getLastPositionTimestamp()?.toMillis() ?? 0) - (a.getLastPositionTimestamp()?.toMillis() ?? 0);
  const here = held.filter((badge) => badge.getIsConnected()).sort(byRecent);
  const quiet = held.filter((badge) => !badge.getIsConnected()).sort(byRecent);

  return (
    <div style={{ margin: '24px 0', ...style }}>
      {here.length > 0 &&
        <>
          <div style={heading}>Staff in or near this room · {here.length}</div>
          {here.map((badge) => <StaffRow key={badge.getSerialNumber()} badge={badge} />)}
        </>}

      {quiet.length > 0 &&
        <>
          <div style={{ ...heading, color: T.muted, marginTop: here.length > 0 ? 20 : 0 }}>
            Last located here, badge not connected · {quiet.length}
          </div>
          {quiet.map((badge) => <StaffRow key={badge.getSerialNumber()} badge={badge} quiet />)}
        </>}
    </div>
  );
}

function StaffRow({ badge, quiet = false }) {
  const [imageFailed, setImageFailed] = useState(false);
  const staff = badge.getBelongsTo();
  const since = formatWhen(badge.getLastPositionTimestamp());
  const heartbeat = heartbeatOf(badge);

  // Here now: since when. Gone quiet: when the badge was last heard from.
  let when = since ? 'since ' + since : null;
  if (quiet) when = heartbeat ? 'heard ' + heartbeat : 'never heard from';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid ' + T.hairlineSoft, opacity: quiet ? 0.7 : 1 }}>
      <img
        height={32}
        width={32}
        src={imageFailed ? DefaultPortrait : (staff.getImagePath() ?? DefaultPortrait)}
        alt=''
        onError={() => setImageFailed(true)}
        style={{ borderRadius: 9999, objectFit: 'cover', flex: 'none' }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, lineHeight: '20px', color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {staff.getName()}
        </div>
        <div style={{ fontFamily: FONT_MONO, fontSize: 12, lineHeight: '16px', color: T.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {staff.getStaffType()?.getName() ?? 'Badge ' + badge.getSerialNumber()}
        </div>
      </div>
      {when &&
        <span style={{ flex: 'none', fontFamily: FONT_MONO, fontSize: 12, lineHeight: '16px', color: T.muted, whiteSpace: 'nowrap' }}>
          {when}
        </span>}
    </div>
  );
}

StaffWidget.propTypes = {
  /** The badges last located in the room; those without a holder are skipped. */
  badges: PropTypes.array,
  style: PropTypes.object
};

StaffRow.propTypes = {
  badge: PropTypes.object.isRequired,
  quiet: PropTypes.bool
};
